import type { ContactCandidateOut, PropertyContactWithDetails, SourceDocumentOut } from '@/client';
import { resolvedContactFields } from '@/components/services/model/serviceContacts';

/** Which list an entry came from, and so which endpoint puts it back. */
export const ArchiveEntryKinds = { Contact: 'contact', Candidate: 'candidate' } as const;
export type ArchiveEntryKind = (typeof ArchiveEntryKinds)[keyof typeof ArchiveEntryKinds];

export interface ArchiveEntry {
	key: string;
	id: number;
	kind: ArchiveEntryKind;
	name: string;
	detail: string | null;
	email: string | null;
	sourceDocument: SourceDocumentOut | null;
}

/**
 * Sorted by name rather than kept in the server's order. The archived list arrives by `updated_at`,
 * which has no `archived_at` behind it and moves when a correction is made, and discarded
 * candidates arrive by name because `processing_status` carries no timestamp at all. Only one of
 * them was recency to begin with, and a restore list is searched rather than read in order.
 */
export const byName = (one: ArchiveEntry, other: ArchiveEntry) => one.name.localeCompare(other.name);

/** A property contact as a list entry: the household's correction where it made one. */
export const contactEntry = (contact: PropertyContactWithDetails): ArchiveEntry => {
	const { name, company, email } = resolvedContactFields(contact);
	return {
		key: `contact:${contact.id}`,
		id: contact.id,
		kind: ArchiveEntryKinds.Contact,
		name,
		detail: company,
		email,
		sourceDocument: null,
	};
};

/** A discarded candidate as an entry, the company standing in where nobody is named. */
export const candidateEntry = (candidate: ContactCandidateOut): ArchiveEntry => ({
	key: `candidate:${candidate.id}`,
	id: candidate.id,
	kind: ArchiveEntryKinds.Candidate,
	name: candidate.name ?? candidate.company ?? 'Unnamed contact',
	detail: candidate.name ? (candidate.company ?? null) : null,
	email: candidate.email ?? null,
	sourceDocument: candidate.source_document ?? null,
});
